import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { buildEmail } from '@adornis/mails/db/templates.js';
import { Message } from '@adornis/mails/server/communication.js';
import { AdornisMailer } from '@adornis/mails/server/mailer.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { getPasswordResetLink } from '@adornis/users/server/password.js';
import { assertNexusPermission } from '@nsc/nexus-permissions/nexus-api/nexus-permission-gate.js';
import { NexusPermission } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { html } from 'lit';

export const sendNexusUserInviteEmail = registerMutation({
  operationName: 'sendNexusUserInviteEmail',
  type: () => Boolean,
  params: [{ name: 'userId', type: () => String }],
  resolve: (userId: string) => async () => {
    await assertNexusPermission(NexusPermission.UserManage, 'user invite');

    const user = await (
      await getRawCollection<EntityData<AdornisUser>>(AdornisUser._collectionName)
    ).findOne({ _id: userId });
    if (!user?._id) {
      throw new Error('User not found');
    }
    if (!user.email?.trim()) {
      throw new Error('User has no email address');
    }

    const resetLink = await getPasswordResetLink('/change-password', {
      userID: user._id,
      userClass: user._class ?? 'AdornisUser',
    });

    const message = await Message.compose({
      subject: 'NSC Nexus: set your password',
      html: buildEmail({
        colorConfig: { highlight: '#f37021' },
        footer: html`<p>NSC Nexus</p>`,
        contents: html`<p>Hello,</p>
          <p>Use this link to set your password for Nexus:</p>
          <p><a href="${resetLink}" style="word-break: break-all;">${resetLink}</a></p>
          <p>If you did not expect this, you can ignore this email.</p>`,
      }),
    });

    await AdornisMailer.getInstance().sendMail(message, new Recipient(user.email.trim()));
    return true;
  },
});
